/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Lobby domain - Lobby card body component
 */

import type { LobbyMetadata } from "@game/domain";
import type React from "react";
import type { LobbySimulation } from "./simulation";

interface LobbyCardBodyProps {
  lobby: LobbyMetadata;
  simulation: LobbySimulation | null;
}

export const LobbyCardBody: React.FC<LobbyCardBodyProps> = ({
  lobby,
  simulation,
}) => {
  const displayCount = simulation?.simulatedPlayerCount ?? lobby.playerCount;
  const displayMax = simulation?.simulatedMaxPlayers ?? lobby.maxPlayers;
  const displayCountdownSeconds =
    simulation?.simulatedSecondsUntilStart ?? lobby.secondsUntilStart;
  const playerPercentage = (displayCount / displayMax) * 100;

  let fillColor: string;
  if (playerPercentage < 40) {
    fillColor = "#4ade80";
  } else if (playerPercentage < 70) {
    const yellowProgress = (playerPercentage - 40) / 30;
    const r = Math.floor(74 + (255 - 74) * yellowProgress);
    const g = Math.floor(222 + (255 - 222) * yellowProgress);
    const b = Math.floor(128 * (1 - yellowProgress));
    fillColor = `rgb(${r}, ${g}, ${b})`;
  } else {
    const redProgress = (playerPercentage - 70) / 30;
    const r = 255;
    const g = Math.floor(255 * (1 - redProgress));
    const b = 0;
    fillColor = `rgb(${r}, ${g}, ${b})`;
  }

  return (
    <div className="lobby-card-body">
      <div className="lobby-card-players">
        <div className="lobby-card-players-label">PLAYERS</div>
        <div className="lobby-card-players-info">
          <span className="lobby-card-players-icon">◆</span>
          <span className="lobby-card-players-count">
            {displayCount}/{displayMax}
          </span>
          <div className="lobby-card-players-bar">
            <div
              className="lobby-card-players-fill"
              style={{
                width: `${playerPercentage}%`,
                backgroundColor: fillColor,
              }}
            />
          </div>
        </div>
      </div>

      <div className="lobby-card-countdown">
        <div className="lobby-card-countdown-label">COUNTDOWN</div>
        <div className="lobby-card-countdown-info">
          <span className="lobby-card-countdown-icon">⏱</span>
          <span className="lobby-countdown-time">
            {displayCountdownSeconds}s
          </span>
        </div>
      </div>

      <div className="lobby-card-metadata">
        <div className="lobby-card-meta-item">
          <span className="lobby-card-meta-icon">⬢</span>
          <span className="lobby-card-meta-mode">
            {lobby.teamMode?.toUpperCase() || "FFA"}
          </span>
        </div>
        <div className="lobby-card-meta-item">
          <span className="lobby-card-meta-icon">◈</span>
          <span className="lobby-card-meta-difficulty">
            {lobby.gameConfig?.difficulty || "NORMAL"}
          </span>
        </div>
      </div>
    </div>
  );
};
